import {
  collection,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore';
import {
  DEFAULT_TENANT_ID,
  type PromoEvent,
  type PromoEventType,
} from '@kawai/shared';
import { db } from '../firebase.js';

const PROMO_EVENTS_COL = 'promo_events';

/**
 * 指定 userId の行動ログを新しい順に取得する（顧客詳細のタイムライン + リッチメニュータップ集計用）。
 *
 * Firestore Rules `promo_events/{autoId}` の read は admin 自テナント。
 * 複合 index `(tenantId ASC, userId ASC, createdAt DESC)` を使用（firestore.indexes.json 追加済）。
 * `max` 未指定なら **全件取得**（タップ回数集計を正確にするため上限を設けない / 1 ユーザー分は
 * 件数が限定的）。件数を絞りたい用途では `max` を渡す。
 */
export async function fetchPromoEventsByUserId(
  tenantId: string,
  userId: string,
  max?: number,
): Promise<PromoEvent[]> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const constraints: QueryConstraint[] = [
    where('tenantId', '==', tid),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
  ];
  if (max !== undefined) constraints.push(fbLimit(max));
  const q = query(collection(db, PROMO_EVENTS_COL), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<PromoEvent, 'id'>),
  }));
}

/**
 * 指定 eventType の行動ログを新しい順に取得する（種別別の分析集計用）。
 *
 * 複合 index `(tenantId ASC, eventType ASC, createdAt DESC)` を使用（firestore.indexes.json 追加済）。
 * リッチメニュータップ分析（richmenu_tap を item 別に集計）で使用。`max` は分析用途で広めの
 * 上限（既定 2000 件）。
 */
export async function fetchPromoEventsByEventType(
  tenantId: string,
  eventType: PromoEventType,
  max = 2000,
): Promise<PromoEvent[]> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const q = query(
    collection(db, PROMO_EVENTS_COL),
    where('tenantId', '==', tid),
    where('eventType', '==', eventType),
    orderBy('createdAt', 'desc'),
    fbLimit(max),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<PromoEvent, 'id'>),
  }));
}

